/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import { EvidencePart, IEvidence } from "@interfaces/Interfaces";
import { IonItem, IonLabel, IonList, IonSelect, IonSelectOption } from "@ionic/react";
import { Text, View } from "@react-pdf/renderer";
import { Capacitor } from "@capacitor/core";
import { updatePopoverTriggerWidth } from "@utils/popoverUtils";
import { getDisplayTitleForPDFExport } from "@utils/stringUtils";
import { useRecoilValueLoadable } from "recoil";
import { evidenceAtom } from "@state/State";
import { format, isValid, startOfDay } from "date-fns";

type EvidenceSelectOption = {
  key: string;
  label: string;
};

type Props = {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  definition: EvidencePart;
  onValueChanged?: (value: any) => void;
  data?: any;
  isDisplay?: boolean;
  isPDF?: boolean;
  pdfStyles: any;
  initialValue?: any;
};

/**
 * IonSelect materialises one custom element per IonSelectOption — large lists stall WKWebView
 * while the evidence modal is presenting. Native <select> delegates the sheet to the OS.
 */
const deferHeavyWorkAfterModalMs = (): number => {
  switch (Capacitor.getPlatform()) {
    case "ios":
      return 550;
    case "android":
      return 320;
    default:
      return 0;
  }
};

const formatEvidenceRowDate = (dateValue: unknown): string => {
  const d = new Date(dateValue as string | number | Date);
  if (!isValid(d)) {
    return "—";
  }
  return format(d, "dd / MM / yyyy");
};

const sortKeyForEvidenceDate = (dateValue: unknown): number => {
  const d = new Date(dateValue as string | number | Date);
  if (!isValid(d)) {
    return 0;
  }
  return startOfDay(d).getTime();
};

const getEvidenceSelectOptions = (evidence: IEvidence[] | null | undefined, ids: string[]): EvidenceSelectOption[] => {
  const list = Array.isArray(evidence) ? evidence : [];
  const idSet = new Set(ids.map((id) => id.trim()).filter((id) => id.length > 0));

  const evidenceWithIds = list.filter((item) => {
    const evidenceJSON = item.evidenceJSON;
    if (!evidenceJSON) return false;

    const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;
    if (onHoldReset === 1) return false;
    if (item.draft) return false;

    const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID ?? "";
    const competenceID = evidenceJSON?.programInfo?.competenceID ?? "";

    const hasProgressCheckCompetenceID = Boolean(progressCheckCompetenceID) && idSet.has(progressCheckCompetenceID);
    const hasCompetenceID = Boolean(competenceID) && idSet.has(competenceID);

    return hasProgressCheckCompetenceID || hasCompetenceID;
  });

  const sorted = [...evidenceWithIds].sort((a, b) => {
    const dayA = sortKeyForEvidenceDate(a.date);
    const dayB = sortKeyForEvidenceDate(b.date);
    if (dayB !== dayA) {
      return dayB - dayA;
    }
    const titleA = (a.evidenceJSON?.Title ?? "").toLowerCase();
    const titleB = (b.evidenceJSON?.Title ?? "").toLowerCase();
    return titleA.localeCompare(titleB);
  });

  return sorted.map((item) => {
    const evidenceJSON = item.evidenceJSON;
    const title = evidenceJSON?.Title ?? "";
    const label = `${title} - ${formatEvidenceRowDate(item.date)}`;
    return { key: String(item.id), label };
  });
};

/** PDF/display rendering runs outside RecoilRoot (@react-pdf/renderer); must not use Recoil hooks. */
const EvidenceSelectStatic: React.FC<Props> = (props) => {
  if (props.isPDF) {
    return (
      <View>
        <Text style={props.pdfStyles.reflectionHeader}>{getDisplayTitleForPDFExport(props.definition)}</Text>
        <Text style={props.pdfStyles.evidenceDate}>{props.data}</Text>
      </View>
    );
  }

  return (
    <div className="readEvidenceBlock">
      <div className="readEvidenceHeader">{props.definition["Display Title"] || props.definition.Name}</div>
      <div className="readEvidenceText">{props.data}</div>
      <div className="mt-4 h-[1px] w-full bg-grey-30" />
    </div>
  );
};

const EvidenceSelectInteractive: React.FC<Props> = (props) => {
  const [value, setValue] = useState<string>(props.initialValue || "");
  const allEvidenceLB = useRecoilValueLoadable(evidenceAtom);
  /** Large evidence catalogs + Ionic native modals deadlock if filtering runs in the paint that opens the modal. */
  const deferOptionsBuild = Capacitor.getPlatform() !== "web";
  const [nativeListValues, setNativeListValues] = useState<EvidenceSelectOption[]>([]);
  const [nativeOptionsReady, setNativeOptionsReady] = useState(!deferOptionsBuild);

  const evidenceForPick = allEvidenceLB.state === "hasValue" ? allEvidenceLB.contents ?? [] : [];
  const listOfValuesSrc = props.definition?.["List of values"] ?? "";

  const memoListValues = useMemo((): EvidenceSelectOption[] => {
    try {
      if (deferOptionsBuild || !listOfValuesSrc.trim()) {
        return [];
      }
      return getEvidenceSelectOptions(evidenceForPick, listOfValuesSrc.split("\n"));
    } catch (error) {
      console.error("Error getting list values", error);
      return [];
    }
  }, [deferOptionsBuild, evidenceForPick, listOfValuesSrc]);

  useEffect(() => {
    if (!deferOptionsBuild) {
      return;
    }

    let cancelled = false;

    const run = (): void => {
      if (cancelled) {
        return;
      }
      try {
        const ev = allEvidenceLB.state === "hasValue" ? allEvidenceLB.contents ?? [] : [];
        if (!listOfValuesSrc.trim()) {
          setNativeListValues([]);
        } else {
          setNativeListValues(getEvidenceSelectOptions(ev, listOfValuesSrc.split("\n")));
        }
      } catch (error) {
        console.error("Error getting list values", error);
        setNativeListValues([]);
      }
      if (!cancelled) {
        setNativeOptionsReady(true);
      }
    };

    setNativeOptionsReady(false);

    const delayMs = deferHeavyWorkAfterModalMs();
    const timeoutId = window.setTimeout(run, delayMs);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [deferOptionsBuild, allEvidenceLB.state, allEvidenceLB.contents, listOfValuesSrc]);

  const listValues = deferOptionsBuild ? nativeListValues : memoListValues;

  const useNativeHtmlSelect = deferOptionsBuild;

  /** Web only: popover on desktop; iOS native path uses <select> instead. */
  const useActionSheet = Capacitor.getPlatform() === "ios";

  const setValueFromPick = (next: string): void => {
    setValue(next);
    props.onValueChanged && props.onValueChanged(next);
  };

  const placeholderText = props.definition["Placeholder Text"] || "Select evidence";

  if (deferOptionsBuild && !nativeOptionsReady) {
    return (
      <div
        className="reflectionActivityTypeContainer min-h-[48px] rounded-md border border-grey-40 bg-white"
        aria-busy="true"
        aria-label={placeholderText}
      />
    );
  }

  if (listValues.length === 0) {
    return (
      <div className="reflectionActivityTypeContainer">
        <div className="reflectionTagsInput text-grey-60">{"No evidence"}</div>
      </div>
    );
  }

  if (useNativeHtmlSelect) {
    return (
      <div className="reflectionActivityTypeContainer">
        <select
          // className="reflectionActivitySelectNative min-h-[44px] w-full max-w-full rounded-md border border-grey-40 bg-white py-3 pl-2 pr-2 text-acc-17px font-acc-600 tracking-[-0.3px] text-grey-90"
          className="custom-select-arrow-large evidenceSelectText w-full cursor-pointer appearance-none truncate bg-white py-2 pl-0 pr-10 font-acc-600 leading-tight text-grey-80"
          style={{ textDecoration: "var(--accessibility-underline-links)" }}
          value={value || ""}
          onChange={(e) => setValueFromPick(e.target.value)}
          aria-label={props.definition["Display Title"] || props.definition.Name || placeholderText}
        >
          <option
            value=""
            disabled
          >
            {placeholderText}
          </option>
          {listValues.map((item) => (
            <option
              key={item.key}
              value={item.label}
            >
              {item.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className="reflectionActivityTypeContainer">
      <IonList
        mode="md"
        className="reflectionActivityList w-full"
        color="white"
      >
        <IonItem
          mode="md"
          className="reflectionActivityItem"
          color="white"
        >
          <IonSelect
            color="white"
            mode="md"
            className="reflectionActivitySelectItem w-full"
            interface={useActionSheet ? "action-sheet" : "popover"}
            justify="start"
            {...(!useActionSheet
              ? {
                  interfaceOptions: {
                    size: "cover",
                    alignment: "start",
                    cssClass: "evidence-select-popover",
                  },
                }
              : {})}
            placeholder={placeholderText}
            value={value || undefined}
            onIonChange={(ev) => setValueFromPick(ev.detail.value)}
            onClick={useActionSheet ? undefined : updatePopoverTriggerWidth}
          >
            {listValues.map((item) => (
              <IonSelectOption
                color="white"
                key={item.key}
                value={item.label}
              >
                <IonLabel aria-label={item.label}>{item.label}</IonLabel>
              </IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>
      </IonList>
    </div>
  );
};

const EvidenceSelect: React.FC<Props> = (props) => {
  if (props.isPDF || props.isDisplay) {
    return <EvidenceSelectStatic {...props} />;
  }
  return <EvidenceSelectInteractive {...props} />;
};

export default EvidenceSelect;
